import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { getDashboardScopeRequest, saveDashboardScopeRequest } from "../../api/authAPI";
import FormCard from "../../Components/FormCard.js";
import catchAxiosError from "../../Util/catchAxiosError.js";
import styles from "./ChangePassword.module.css";

const toggleId = (list, id) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);

/** Admin: which firms/units the Dashboard figures count, for every user. None ticked = all. */
function DashboardScopeCard() {
    const [firms, setFirms] = useState([]);
    const [units, setUnits] = useState([]);
    const [firmIds, setFirmIds] = useState([]);
    const [unitIds, setUnitIds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const ctrl = new AbortController();
        getDashboardScopeRequest(ctrl.signal)
            .then((d) => {
                setFirms(d?.firms || []);
                setUnits(d?.units || []);
                setFirmIds(d?.firmIds || []);
                setUnitIds(d?.unitIds || []);
            })
            .catch((err) => catchAxiosError(err, "Failed to load dashboard setting."))
            .finally(() => { if (!ctrl.signal.aborted) setLoading(false); });
        return () => ctrl.abort();
    }, []);

    // Units shown = units of the ticked firms, or every unit when no firm is ticked.
    const shownFirms = firmIds.length ? firms.filter((f) => firmIds.includes(f.firmid)) : firms;

    const onFirm = (id) => {
        const next = toggleId(firmIds, id);
        setFirmIds(next);
        // Drop ticked units whose firm is no longer in scope.
        if (next.length) {
            const keep = new Set(units.filter((u) => next.includes(u.firmid)).map((u) => u.unitid));
            setUnitIds((prev) => prev.filter((uid) => keep.has(uid)));
        }
    };

    const save = async () => {
        setSaving(true);
        try {
            await saveDashboardScopeRequest({ firmIds, unitIds });
            toast.success(
                `Dashboard now counts ${firmIds.length ? `${firmIds.length} firm(s)` : "all firms"}, ` +
                `${unitIds.length ? `${unitIds.length} unit(s)` : "all units"}.`,
            );
        } catch (err) {
            catchAxiosError(err, "Failed to save dashboard setting.");
        } finally {
            setSaving(false);
        }
    };

    const box = (checked, onChange, label, key) => (
        <label key={key} style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontSize: "var(--fs-body)" }}>
            <input type="checkbox" checked={checked} onChange={onChange} disabled={loading || saving} />
            {label}
        </label>
    );

    return (
        <FormCard maxWidth={480} style={{ marginTop: "1.5rem" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                <div>
                    <p className={styles.prefLabel}>Dashboard Figures</p>
                    <p className={styles.prefHint}>
                        Which firms and units the Dashboard adds up, for every user. Leave all unticked to count
                        everything. Users still only see firms and units they have access to.
                    </p>
                </div>

                <div>
                    <p style={{ fontSize: "var(--fs-body)", fontWeight: 500, marginBottom: "0.35rem" }}>
                        Firms {firmIds.length === 0 && <span className={styles.prefHint}>(all firms)</span>}
                    </p>
                    <div style={{ display: "flex", flexDirection: "column", gap: "0.3rem" }}>
                        {firms.map((f) => box(firmIds.includes(f.firmid), () => onFirm(f.firmid), f.firmname, f.firmid))}
                    </div>
                </div>

                <div>
                    <p style={{ fontSize: "var(--fs-body)", fontWeight: 500, marginBottom: "0.35rem" }}>
                        Units {unitIds.length === 0 && <span className={styles.prefHint}>(all units)</span>}
                    </p>
                    {shownFirms.map((f) => {
                        const fUnits = units.filter((u) => u.firmid === f.firmid);
                        if (!fUnits.length) return null;
                        return (
                            <div key={f.firmid} style={{ marginBottom: "0.5rem" }}>
                                <p className={styles.prefHint} style={{ marginBottom: "0.2rem" }}>{f.firmname}</p>
                                <div style={{ display: "flex", flexDirection: "column", gap: "0.3rem", paddingLeft: "0.75rem" }}>
                                    {fUnits.map((u) => box(
                                        unitIds.includes(u.unitid),
                                        () => setUnitIds((prev) => toggleId(prev, u.unitid)),
                                        `${u.unit_name} (${u.unit_code})`,
                                        u.unitid,
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                    <button type="button" className={styles.saveBtn} disabled={loading || saving} onClick={save}>
                        {saving ? "Saving…" : "Save Setting"}
                    </button>
                </div>
            </div>
        </FormCard>
    );
}

export default DashboardScopeCard;
